import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import { OrderWorkRecord } from "./types";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  CloseBtn,
  ModalDivider,
  ModalBody,
  CancelBtn,
  SaveBtn,
  ModalFooter
} from "./modalSharedStyles";

type NotifyMemberModalProps = {
  record: OrderWorkRecord;
  employees: any[];
  onClose: () => void;
  onNotify: (selectedIds: number[]) => void;
};

const NotifyMemberModal: React.FC<NotifyMemberModalProps> = ({ record, employees, onClose, onNotify }) => {
  const assignedList = useMemo(() => {
    if (record.assignedMembers && record.assignedMembers.length > 0) {
      return record.assignedMembers.map(m => {
        const emp = employees.find(e => e.id === m.employee);
        return {
          id: m.employee,
          name: emp?.name || "Staff",
          role: m.role || record.role || "Staff"
        };
      });
    }
    if (record.teamMembers && record.teamMembers.length > 0) {
      return record.teamMembers.map(id => {
        const emp = employees.find(e => e.id === id);
        return {
          id,
          name: emp?.name || "Staff",
          role: record.role || "Staff"
        };
      });
    }
    return [];
  }, [record, employees]);

  // Select all by default
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>(() => {
    return assignedList.map(m => m.id);
  });

  const handleToggle = (id: number) => {
    setSelectedMemberIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSend = () => {
    if (selectedMemberIds.length === 0) {
      showErrorToast("Please select at least one team member");
      return;
    }
    onNotify(selectedMemberIds);
  };

  return (
    <Overlay onClick={onClose}>
      <NarrowModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Notify Team Members</ModalTitle>
          <CloseBtn type="button" onClick={onClose}>×</CloseBtn>
        </ModalHeader>
        <ModalDivider />
        <CustomModalBody>
          <DescriptionText>
            Select the team member(s) you want to notify for order <strong>{record.orderNo}</strong>:
          </DescriptionText>
          <OptionsContainer>
            {assignedList.map(m => {
              const isChecked = selectedMemberIds.includes(m.id);
              return (
                <OptionLabel
                  key={m.id}
                  $selected={isChecked}
                >
                  <CheckboxInput
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggle(m.id)}
                  />
                  <div>
                    <MemberName>{m.name}</MemberName>
                    <MemberRole>Role: {m.role}</MemberRole>
                  </div>
                </OptionLabel>
              );
            })}
          </OptionsContainer>
        </CustomModalBody>
        <ModalDivider />
        <ModalFooter>
          <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
          <SaveBtn type="button" onClick={handleSend}>
            Send Notification
          </SaveBtn>
        </ModalFooter>
      </NarrowModalCard>
    </Overlay>
  );
};

export default NotifyMemberModal;

// ─── Local Styled Components ──────────────────────────────────────────────────

const NarrowModalCard = styled(ModalCard)`
  max-width: 450px;
`;

const CustomModalBody = styled(ModalBody)`
  padding: 24px;
`;

const DescriptionText = styled.p`
  margin-bottom: 18px;
  font-size: 14px;
  color: rgba(23, 23, 23, 0.7);
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionLabel = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid ${p => p.$selected ? "#c94daa" : "rgba(23, 23, 23, 0.08)"};
  background: ${p => p.$selected ? "rgba(201, 77, 170, 0.04)" : "transparent"};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CheckboxInput = styled.input`
  accent-color: #c94daa;
  cursor: pointer;
`;

const MemberName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: rgba(23, 23, 23, 0.9);
`;

const MemberRole = styled.div`
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;
